import {
  BUILDINGS,
  BuildingType,
  RECIPES,
  Recipe,
  RESEARCHES,
  ResearchId,
  ResearchRequirement,
  ItemType,
  RepeatableResearchType,
} from './constants';

export type RepeatableResearchLevels = Partial<Record<ResearchId, number>>;

export type ResearchProgress = Partial<
  Record<ResearchId, Partial<Record<ItemType, number>>>
>;

export interface ResearchState {
  readonly unlockedResearch: Partial<Record<ResearchId, boolean>>;
  readonly repeatableResearchLevels: RepeatableResearchLevels;
  readonly selectedResearch: ResearchId | null;
  readonly progress: ResearchProgress;
  readonly queue: ResearchId[];
}

export interface SerializedResearchState {
  readonly unlockedResearch?: readonly ResearchId[];
  readonly selectedResearch?: ResearchId | null;
  readonly progress?: ResearchProgress;
  readonly repeatableResearchLevels?: RepeatableResearchLevels;
  readonly queue?: ResearchId[];
}

export const createInitialResearchState = (): ResearchState => ({
  unlockedResearch: {},
  repeatableResearchLevels: {},
  selectedResearch: null,
  progress: {},
  queue: [],
});

export const serializeResearchState = (state: ResearchState): ResearchId[] =>
  Object.entries(state.unlockedResearch)
    .filter(([, isUnlocked]) => isUnlocked)
    .map(([researchId]) => researchId as ResearchId);

export const serializeResearchProgress = (
  state: ResearchState
): ResearchProgress => {
  const output: ResearchProgress = {};
  Object.entries(state.progress).forEach(([researchId, itemMap]) => {
    output[researchId as ResearchId] = itemMap ? { ...itemMap } : {};
  });
  return output;
};

export const serializeRepeatableResearchLevels = (
  state: ResearchState
): RepeatableResearchLevels => {
  const entries = Object.entries(state.repeatableResearchLevels).filter(
    ([, value]) => typeof value === 'number' && value > 0
  );

  if (entries.length === 0) {
    return {};
  }

  return entries.reduce<RepeatableResearchLevels>(
    (acc, [researchId, level]) => {
      acc[researchId as ResearchId] = level as number;
      return acc;
    },
    {}
  );
};

const hasRemainingResearchWork = (
  state: ResearchState,
  researchId: ResearchId
): boolean => {
  const repeatableConfig = getRepeatableResearchConfig(researchId);

  if (!repeatableConfig) {
    return !isResearchUnlocked(state, researchId);
  }

  const currentLevel = getRepeatableResearchLevel(state, researchId);
  if (
    repeatableConfig.maxLevel !== undefined &&
    currentLevel >= repeatableConfig.maxLevel
  ) {
    return false;
  }

  return true;
};

const normalizeResearchQueue = (
  state: ResearchState,
  queue: ResearchId[]
): ResearchId[] => {
  const seen = new Set<ResearchId>();

  return queue.filter(researchId => {
    if (seen.has(researchId)) {
      return false;
    }

    const research = RESEARCHES[researchId];
    if (!research) {
      return false;
    }

    if (!hasRemainingResearchWork(state, researchId)) {
      return false;
    }

    seen.add(researchId);
    return true;
  });
};

export const setResearchQueue = (
  state: ResearchState,
  queue: ResearchId[]
): ResearchState => {
  const normalizedQueue = normalizeResearchQueue(state, queue);
  const nextSelected = normalizedQueue[0] ?? null;

  if (
    nextSelected === state.selectedResearch &&
    normalizedQueue.length === state.queue.length &&
    normalizedQueue.every((id, index) => state.queue[index] === id)
  ) {
    return state;
  }

  return {
    ...state,
    queue: normalizedQueue,
    selectedResearch: nextSelected,
  };
};

export const deserializeResearchState = (
  data?: readonly ResearchId[] | SerializedResearchState
): ResearchState => {
  const base = createInitialResearchState();

  if (!data) {
    return base;
  }

  if (Array.isArray(data)) {
    const unlockedResearch = data.reduce<Partial<Record<ResearchId, boolean>>>(
      (acc, id) => {
        acc[id as ResearchId] = true;
        return acc;
      },
      {}
    );
    return {
      ...base,
      unlockedResearch,
    };
  }

  const serializedState = data as SerializedResearchState;
  const unlockedResearch = (serializedState.unlockedResearch ?? []).reduce<
    Partial<Record<ResearchId, boolean>>
  >((acc, id) => {
    acc[id as ResearchId] = true;
    return acc;
  }, {});

  const progress: ResearchProgress = {};
  if (serializedState.progress) {
    Object.entries(serializedState.progress).forEach(([id, map]) => {
      progress[id as ResearchId] = map ? { ...map } : {};
    });
  }

  const repeatableResearchLevels: RepeatableResearchLevels = {};
  if (serializedState.repeatableResearchLevels) {
    Object.entries(serializedState.repeatableResearchLevels).forEach(
      ([id, level]) => {
        if (typeof level === 'number' && level > 0) {
          repeatableResearchLevels[id as ResearchId] = level;
        }
      }
    );
  }

  Object.entries(unlockedResearch).forEach(([id, isUnlocked]) => {
    if (!isUnlocked) {
      return;
    }

    const researchId = id as ResearchId;
    if (
      getRepeatableConfig(researchId) &&
      !repeatableResearchLevels[researchId]
    ) {
      repeatableResearchLevels[researchId] = 1;
    }
  });

  const baseState: ResearchState = {
    unlockedResearch,
    selectedResearch: null,
    progress,
    repeatableResearchLevels,
    queue: [],
  };

  const queuedResearch = Array.isArray(serializedState.queue)
    ? serializedState.queue
    : serializedState.selectedResearch
      ? [serializedState.selectedResearch]
      : [];

  return setResearchQueue(baseState, queuedResearch);
};

const buildingUnlockMap: Partial<Record<BuildingType, ResearchId>> = {};
const recipeUnlockMap: Partial<Record<Recipe['id'], ResearchId>> = {};
const repeatableResearchByType = new Map<RepeatableResearchType, ResearchId>();

Object.values(RESEARCHES).forEach(research => {
  research.unlocks.buildings?.forEach(buildingId => {
    buildingUnlockMap[buildingId] = research.id;
  });

  research.unlocks.recipes?.forEach(recipeId => {
    recipeUnlockMap[recipeId] = research.id;
  });

  if (research.repeatable) {
    repeatableResearchByType.set(research.repeatable.type, research.id);
  }
});

const getResearchDefinition = (researchId: ResearchId) => {
  return RESEARCHES[researchId];
};

const getRepeatableConfig = (researchId: ResearchId) => {
  return getResearchDefinition(researchId)?.repeatable ?? null;
};

const getRepeatableResearchIdForType = (
  type: RepeatableResearchType
): ResearchId | null => {
  return repeatableResearchByType.get(type) ?? null;
};

export const getRepeatableResearchLevel = (
  state: ResearchState,
  researchId: ResearchId
): number => {
  return state.repeatableResearchLevels[researchId] ?? 0;
};

export const isResearchRepeatable = (researchId: ResearchId): boolean => {
  return Boolean(getRepeatableConfig(researchId));
};

export const getRepeatableResearchConfig = (researchId: ResearchId) => {
  return getRepeatableConfig(researchId);
};

export const getRepeatableResearchLevelForType = (
  state: ResearchState,
  type: RepeatableResearchType
): number => {
  const researchId = getRepeatableResearchIdForType(type);
  if (!researchId) {
    return 0;
  }
  return getRepeatableResearchLevel(state, researchId);
};

const getRepeatableMultiplierForType = (
  state: ResearchState,
  type: RepeatableResearchType
): number => {
  const researchId = getRepeatableResearchIdForType(type);
  if (!researchId) {
    return 1;
  }

  const config = getRepeatableConfig(researchId);
  if (!config) {
    return 1;
  }

  const level = getRepeatableResearchLevel(state, researchId);
  if (level <= 0) {
    return 1;
  }

  if (config.type === 'output-capacity' || config.type === 'belt-stacking') {
    return Math.pow(2, level);
  }

  if (config.type === 'assembler-speed') {
    return Math.pow(1 + config.bonusPerLevel, level);
  }

  return 1 + level * config.bonusPerLevel;
};

export const getMinerSpeedMultiplier = (state: ResearchState): number => {
  return getRepeatableMultiplierForType(state, 'miner-speed');
};

export const getBeltSpeedMultiplier = (state: ResearchState): number => {
  return getRepeatableMultiplierForType(state, 'belt-speed');
};

export const getGeneratorOutputMultiplier = (state: ResearchState): number => {
  return getRepeatableMultiplierForType(state, 'generator-output');
};

export const getAssemblerSpeedMultiplier = (state: ResearchState): number => {
  return getRepeatableMultiplierForType(state, 'assembler-speed');
};

export const getFactorySpeedMultiplier = (state: ResearchState): number => {
  const assemblerMultiplier = getAssemblerSpeedMultiplier(state);
  const factoryResearchMultiplier = getRepeatableMultiplierForType(
    state,
    'factory-speed'
  );

  return assemblerMultiplier * factoryResearchMultiplier;
};

export const getOutputCapacityMultiplier = (state: ResearchState): number => {
  return getRepeatableMultiplierForType(state, 'output-capacity');
};

export const getBeltCapacityMultiplier = (state: ResearchState): number => {
  return getRepeatableMultiplierForType(state, 'belt-stacking');
};

export const getRequiredResearchForBuilding = (
  buildingType: BuildingType
): ResearchId | null => {
  return buildingUnlockMap[buildingType] ?? null;
};

export const getRequiredResearchForRecipe = (
  recipeId: Recipe['id']
): ResearchId | null => {
  return recipeUnlockMap[recipeId] ?? null;
};

export const isResearchUnlocked = (
  state: ResearchState,
  researchId: ResearchId
): boolean => {
  return Boolean(state.unlockedResearch[researchId]);
};

export const unlockResearchId = (
  state: ResearchState,
  researchId: ResearchId
): ResearchState => {
  const research = getResearchDefinition(researchId);
  if (!research) {
    return state;
  }

  const repeatableConfig = getRepeatableConfig(researchId);
  const currentLevel = getRepeatableResearchLevel(state, researchId);
  const maxLevel = repeatableConfig?.maxLevel;

  if (!repeatableConfig && isResearchUnlocked(state, researchId)) {
    return state;
  }

  if (repeatableConfig && maxLevel !== undefined && currentLevel >= maxLevel) {
    return state;
  }

  // Remove progress for this research since it's being unlocked

  const remainingProgress = { ...state.progress };
  delete remainingProgress[researchId];

  const unlockedResearch = {
    ...state.unlockedResearch,
    [researchId]: true,
  };

  const nextLevel = repeatableConfig ? currentLevel + 1 : 1;

  const repeatableResearchLevels = repeatableConfig
    ? {
        ...state.repeatableResearchLevels,
        [researchId]: nextLevel,
      }
    : state.repeatableResearchLevels;

  const shouldClearSelection =
    state.selectedResearch === researchId &&
    (!repeatableConfig ||
      (repeatableConfig.maxLevel !== undefined &&
        nextLevel >= repeatableConfig.maxLevel));

  const nextState: ResearchState = {
    ...state,
    unlockedResearch,
    repeatableResearchLevels,
    progress: remainingProgress,
    selectedResearch: shouldClearSelection ? null : state.selectedResearch,
  };

  const cleanedQueue = nextState.queue.filter(id => id !== researchId);

  return setResearchQueue(nextState, cleanedQueue);
};

export const isBuildingUnlocked = (
  state: ResearchState,
  buildingType: BuildingType
): boolean => {
  const requiredResearch = getRequiredResearchForBuilding(buildingType);
  if (!requiredResearch) {
    return true;
  }
  return isResearchUnlocked(state, requiredResearch);
};

export const isRecipeUnlocked = (
  state: ResearchState,
  recipeId: Recipe['id']
): boolean => {
  const requiredResearch = getRequiredResearchForRecipe(recipeId);
  if (!requiredResearch) {
    return true;
  }
  return isResearchUnlocked(state, requiredResearch);
};

export const getUnlockedBuildings = (state: ResearchState): BuildingType[] => {
  return Object.values(BUILDINGS)
    .map(building => building.id)
    .filter(buildingType => isBuildingUnlocked(state, buildingType));
};

export const getUnlockedRecipes = (state: ResearchState): Recipe['id'][] => {
  return Object.values(RECIPES)
    .map(recipe => recipe.id)
    .filter(recipeId => isRecipeUnlocked(state, recipeId));
};

export const getResearchRequirements = (
  researchId: ResearchId,
  level: number = 0
): readonly ResearchRequirement[] => {
  const research = getResearchDefinition(researchId);
  if (!research || !research.requirements) {
    return [];
  }

  const repeatableConfig = getRepeatableConfig(researchId);
  if (!repeatableConfig) {
    return research.requirements;
  }

  const growth = repeatableConfig.costGrowth ?? 1;
  const multiplier = growth <= 1 ? 1 : Math.pow(growth, level);

  if (multiplier === 1) {
    return research.requirements;
  }

  return research.requirements.map(requirement => {
    const scaledAmount = Math.ceil(requirement.amount * multiplier);
    return {
      ...requirement,
      amount: Math.max(requirement.amount, scaledAmount),
    };
  });
};

export const getNextResearchRequirements = (
  state: ResearchState,
  researchId: ResearchId
): readonly ResearchRequirement[] => {
  const research = getResearchDefinition(researchId);
  if (!research) {
    return [];
  }

  const repeatableConfig = getRepeatableConfig(researchId);
  if (!repeatableConfig) {
    return isResearchUnlocked(state, researchId)
      ? []
      : getResearchRequirements(researchId, 0);
  }

  const currentLevel = getRepeatableResearchLevel(state, researchId);
  if (
    repeatableConfig.maxLevel !== undefined &&
    currentLevel >= repeatableConfig.maxLevel
  ) {
    return [];
  }

  return getResearchRequirements(researchId, currentLevel);
};

export const getResearchProgress = (
  state: ResearchState,
  researchId: ResearchId,
  itemType?: ItemType
): number => {
  const itemMap = state.progress[researchId];
  if (!itemMap) {
    return 0;
  }
  if (!itemType) {
    return Object.values(itemMap).reduce((sum, value = 0) => sum + value, 0);
  }
  return itemMap[itemType] ?? 0;
};

const collectResearchQueuePath = (
  state: ResearchState,
  researchId: ResearchId,
  output: ResearchId[],
  visiting: Set<ResearchId>
): void => {
  if (visiting.has(researchId)) {
    return;
  }

  visiting.add(researchId);

  const research = RESEARCHES[researchId];
  if (!research) {
    return;
  }

  research.prerequisites.forEach(prerequisiteId => {
    collectResearchQueuePath(state, prerequisiteId, output, visiting);
  });

  if (hasRemainingResearchWork(state, researchId)) {
    output.push(researchId);
  }
};

export const buildResearchQueueForTarget = (
  state: ResearchState,
  researchId: ResearchId
): ResearchId[] => {
  const queue: ResearchId[] = [];
  collectResearchQueuePath(state, researchId, queue, new Set());
  return queue;
};

export const queueResearchTarget = (
  state: ResearchState,
  researchId: ResearchId | null
): ResearchState => {
  if (!researchId) {
    return setResearchQueue(state, []);
  }

  const queue = buildResearchQueueForTarget(state, researchId);
  return setResearchQueue(state, queue);
};

export const setSelectedResearch = (
  state: ResearchState,
  researchId: ResearchId | null
): ResearchState => {
  return queueResearchTarget(state, researchId);
};

export const getActiveResearchId = (
  state: ResearchState
): ResearchId | null => {
  return state.queue[0] ?? null;
};

export const advanceResearchQueue = (state: ResearchState): ResearchState => {
  if (state.queue.length === 0) {
    return setResearchQueue(state, []);
  }

  return setResearchQueue(state, state.queue.slice(1));
};

export const addResearchProgress = (
  state: ResearchState,
  researchId: ResearchId,
  itemType: ItemType,
  amount: number
): {
  state: ResearchState;
  accepted: boolean;
  completed: boolean;
} => {
  if (amount <= 0) {
    return { state, accepted: false, completed: false };
  }

  const research = getResearchDefinition(researchId);
  if (!research) {
    return { state, accepted: false, completed: false };
  }

  const repeatableConfig = getRepeatableConfig(researchId);
  const currentLevel = repeatableConfig
    ? getRepeatableResearchLevel(state, researchId)
    : 0;

  if (!repeatableConfig && isResearchUnlocked(state, researchId)) {
    return { state, accepted: false, completed: false };
  }

  if (
    repeatableConfig &&
    repeatableConfig.maxLevel !== undefined &&
    currentLevel >= repeatableConfig.maxLevel
  ) {
    return { state, accepted: false, completed: false };
  }

  const requirements = getResearchRequirements(researchId, currentLevel);
  if (requirements.length === 0) {
    return { state, accepted: false, completed: false };
  }

  const requirement = requirements.find(req => req.item === itemType);
  if (!requirement) {
    return { state, accepted: false, completed: false };
  }

  const currentForResearch = state.progress[researchId] ?? {};
  const currentAmount = currentForResearch[itemType] ?? 0;
  if (currentAmount >= requirement.amount) {
    return { state, accepted: false, completed: false };
  }

  const newAmount = Math.min(requirement.amount, currentAmount + amount);
  const updatedProgress: ResearchProgress = {
    ...state.progress,
    [researchId]: {
      ...currentForResearch,
      [itemType]: newAmount,
    },
  };

  const completed = requirements.every(req => {
    const recorded = updatedProgress[researchId]?.[req.item] ?? 0;
    return recorded >= req.amount;
  });

  return {
    state: {
      ...state,
      progress: updatedProgress,
    },
    accepted: true,
    completed,
  };
};
